import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import TempleCard from './TempleCard';
import './MapView.css';

const FAITH_COLORS = {
  hindu:    '#b5500a',
  buddhist: '#1a6b9a',
  jain:     '#3a7a20',
};

function FitBounds({ temples }) {
  const map = useMap();
  useEffect(() => {
    const pts = temples.filter(t => t.lat && t.lng).map(t => [t.lat, t.lng]);
    if (pts.length === 0) return;
    if (pts.length === 1) { map.setView(pts[0], 13); return; }
    map.fitBounds(pts, { padding: [48, 48], maxZoom: 14 });
  }, [temples]); // eslint-disable-line
  return null;
}

export default function MapView({ temples, lang, loading, error }) {
  const [selected, setSelected] = useState(null);
  const pts = temples ? temples.filter(t => t.lat && t.lng) : [];

  const L = (en, hi) => lang === 'en' ? en : hi;

  if (loading) return (
    <div className="map-state"><div className="spinner" /><p>{L('Loading…','लोड हो रहा है…')}</p></div>
  );
  if (error) return (
    <div className="map-state"><p style={{ color: '#a00' }}>⚠️ {error}</p></div>
  );
  if (!temples || temples.length === 0) return (
    <div className="map-state">
      <div style={{ fontSize: 40 }}>🗺️</div>
      <p>{L('Select a state or circuit to see temple locations.','मंदिर स्थानों के लिए राज्य या परिपथ चुनें।')}</p>
    </div>
  );

  return (
    <div className="map-outer">
      <MapContainer
        center={[11.5, 79.0]}
        zoom={7}
        className="map-leaflet"
        zoomControl={true}
      >
        <TileLayer
          attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, © <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        />
        <FitBounds temples={pts} />
        {pts.map(t => (
          <CircleMarker
            key={t.id}
            center={[t.lat, t.lng]}
            radius={selected?.id === t.id ? 10 : 7}
            pathOptions={{
              fillColor: FAITH_COLORS[t.faith] || '#888',
              color: selected?.id === t.id ? '#fff' : 'rgba(255,255,255,0.6)',
              weight: selected?.id === t.id ? 2.5 : 1,
              fillOpacity: 0.88,
            }}
            eventHandlers={{ click: () => setSelected(t) }}
          >
            <Tooltip direction="top" offset={[0, -6]}>
              <span className="map-tooltip-name">{lang === 'en' ? t.name_en : (t.name_hi || t.name_en)}</span>
              <span className="map-tooltip-city">{t.city}</span>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>

      {selected && (
        <div className="map-card-panel fade-in">
          <button className="map-card-close" onClick={() => setSelected(null)} title="Close">✕</button>
          <TempleCard temple={selected} lang={lang} defaultOpen={true} />
        </div>
      )}

      <div className="map-legend">
        {Object.entries(FAITH_COLORS).map(([faith, color]) => (
          <span key={faith} className="map-legend-item">
            <span className="map-legend-dot" style={{ background: color }} />
            {lang === 'en' ? faith.charAt(0).toUpperCase() + faith.slice(1) : { hindu: 'हिंदू', buddhist: 'बौद्ध', jain: 'जैन' }[faith]}
          </span>
        ))}
        <span className="map-legend-count">{pts.length} {L('sites','स्थल')}</span>
      </div>
    </div>
  );
}
